// Jul-2026 — Issued Items Report
// Detailed Issue Voucher lines with Project / Warehouse / Party dimensions,
// quantity & amount. Group-by rendering with subtotals + Excel export.
// Endpoints: POST /api/reports/issued-items/summary | /export.xlsx

import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-helpers";
import { Card } from "@/components/ui/card";
import { useClientPagination } from "@/hooks/useClientPagination";
import { PaginationControls } from "@/components/PaginationControls";
import { FileSpreadsheet, Filter, PackageOpen, RotateCw } from "lucide-react";

const EMPTY = { from_date: "", to_date: "", branch_id: "", project_id: "", warehouse_id: "", party_type: "", line_type: "", issue_type: "", status: "Approved" };
const selCls = "w-full border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";
const lblCls = "text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1";

const GROUPS = {
  "": null,
  project: { key: "project_name", label: "Project" },
  warehouse: { key: "warehouse_name", label: "Warehouse" },
  party: { key: "party_name", label: "Party" },
};

export default function IssuedItemsReport() {
  const [filters, setFilters] = useState(EMPTY);
  const [groupBy, setGroupBy] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [branches, setBranches] = useState([]);
  const [projects, setProjects] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [b, p, w] = await Promise.all([api.get("/branches"), api.get("/projects-picker"), api.get("/warehouses")]);
        setBranches(Array.isArray(b.data) ? b.data : b.data?.items || []);
        setProjects(Array.isArray(p.data) ? p.data : p.data?.items || []);
        setWarehouses(Array.isArray(w.data) ? w.data : w.data?.items || []);
      } catch { /* dropdowns optional */ }
    })();
    run(EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function run(f = filters) {
    setLoading(true);
    try {
      const res = await api.post("/reports/issued-items/summary", f);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load the report");
    } finally { setLoading(false); }
  }

  async function doExport() {
    setExporting(true);
    try {
      const res = await api.post("/reports/issued-items/export.xlsx", filters, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "issued_items_report.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Excel export failed");
    } finally { setExporting(false); }
  }

  const rows = data?.rows || [];
  const pg = useClientPagination(rows, { initialPageSize: 50 });
  const t = data?.totals || {};
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  // Group the current page's rows client-side for subtotal rendering.
  const grouped = useMemo(() => {
    const g = GROUPS[groupBy];
    if (!g) return null;
    const map = new Map();
    for (const r of pg.paged) {
      const key = r[g.key] || "—";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return [...map.entries()];
  }, [pg.paged, groupBy]);

  const cards = [
    { key: "vouchers", label: "Vouchers", tone: "border-slate-300 bg-slate-100 text-slate-900" },
    { key: "lines", label: "Lines", tone: "border-sky-200 bg-sky-50 text-sky-900" },
    { key: "material_qty", label: "Material Qty", tone: "border-emerald-200 bg-emerald-50 text-emerald-900" },
    { key: "asset_count", label: "Assets Issued", tone: "border-violet-200 bg-violet-50 text-violet-900" },
    { key: "total_amount", label: "Total Amount", tone: "border-orange-200 bg-orange-50 text-orange-900", money: true },
  ];

  const Row = ({ r, i }) => (
    <tr data-testid={`ii-row-${i}`} className="border-t border-slate-100 hover:bg-orange-50/40">
      <td className="px-3 py-2 whitespace-nowrap font-bold text-slate-900">{r.voucher_number}</td>
      <td className="px-3 py-2 whitespace-nowrap">{r.voucher_date}</td>
      <td className="px-3 py-2 whitespace-nowrap">{r.branch_name}</td>
      <td className="px-3 py-2">{r.project_name}</td>
      <td className="px-3 py-2">{r.warehouse_name}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className="text-[10px] uppercase font-bold text-slate-400 mr-1">{r.party_type}</span>{r.party_name}
      </td>
      <td className="px-3 py-2">{r.item_name}</td>
      <td className="px-3 py-2 font-mono text-[10px]">{r.code}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className={`inline-block border px-1.5 py-0.5 text-[10px] font-bold uppercase ${r.line_type === "Material" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-violet-100 text-violet-800 border-violet-200"}`}>{r.line_type}</span>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">{r.issue_type}</td>
      <td className="px-3 py-2 text-right tabular-nums">{r.quantity}</td>
      <td className="px-3 py-2">{r.unit}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatINR(r.unit_rate)}</td>
      <td className="px-3 py-2 text-right tabular-nums font-bold">{formatINR(r.amount)}</td>
    </tr>
  );

  const SubTotal = ({ label, list }) => (
    <tr className="bg-slate-50 border-t border-slate-200">
      <td colSpan={10} className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-600">
        {label} — {list.length} lines
      </td>
      <td className="px-3 py-2 text-right tabular-nums font-bold">
        {list.filter((r) => r.line_type === "Material").reduce((s, r) => s + r.quantity, 0)}
      </td>
      <td colSpan={2} className="px-3 py-2 text-right text-[10px] uppercase font-bold text-slate-500">Subtotal</td>
      <td className="px-3 py-2 text-right tabular-nums font-bold">{formatINR(list.reduce((s, r) => s + r.amount, 0))}</td>
    </tr>
  );

  return (
    <div data-testid="issued-items-page">
      <PageHeader
        eyebrow="Documents & Reports"
        title="Issued Items Report"
        subtitle="Every Issue Voucher line — material and asset — with project, warehouse and party (Member / Client / Vendor) dimensions, quantities and amounts."
        actions={
          <button data-testid="ii-export-xlsx" disabled={exporting || loading} onClick={doExport}
            className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
            <FileSpreadsheet size={14} /> {exporting ? "Exporting…" : "Excel"}
          </button>
        }
      />

      <Card className="mb-4">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-2"><Filter size={12} /> Filters</h3>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 items-end">
          <div><div className={lblCls}>From Date</div>
            <input data-testid="ii-from" type="date" value={filters.from_date} onChange={(e) => set("from_date", e.target.value)} className={selCls} /></div>
          <div><div className={lblCls}>To Date</div>
            <input data-testid="ii-to" type="date" value={filters.to_date} onChange={(e) => set("to_date", e.target.value)} className={selCls} /></div>
          <div><div className={lblCls}>Branch</div>
            <select data-testid="ii-branch" value={filters.branch_id} onChange={(e) => set("branch_id", e.target.value)} className={selCls}>
              <option value="">All Branches</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.branch_name || b.name}</option>)}
            </select></div>
          <div><div className={lblCls}>Project</div>
            <select data-testid="ii-project" value={filters.project_id} onChange={(e) => set("project_id", e.target.value)} className={selCls}>
              <option value="">All Projects</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name || p.project_name}</option>)}
            </select></div>
          <div><div className={lblCls}>Warehouse</div>
            <select data-testid="ii-warehouse" value={filters.warehouse_id} onChange={(e) => set("warehouse_id", e.target.value)} className={selCls}>
              <option value="">All Warehouses</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.warehouse_name || w.name}</option>)}
            </select></div>
          <div><div className={lblCls}>Party Type</div>
            <select data-testid="ii-party-type" value={filters.party_type} onChange={(e) => set("party_type", e.target.value)} className={selCls}>
              <option value="">All Parties</option>
              <option value="Member">Member</option>
              <option value="Client">Client</option>
              <option value="Vendor">Vendor</option>
            </select></div>
          <div><div className={lblCls}>Line Type</div>
            <select data-testid="ii-line-type" value={filters.line_type} onChange={(e) => set("line_type", e.target.value)} className={selCls}>
              <option value="">Material + Asset</option>
              <option value="Material">Material</option>
              <option value="Asset">Asset</option>
            </select></div>
          <div><div className={lblCls}>Issue Type</div>
            <select data-testid="ii-issue-type" value={filters.issue_type} onChange={(e) => set("issue_type", e.target.value)} className={selCls}>
              <option value="">All</option>
              <option value="Consumable">Consumable</option>
              <option value="Returnable">Returnable</option>
            </select></div>
          <div><div className={lblCls}>Voucher Status</div>
            <select data-testid="ii-status" value={filters.status} onChange={(e) => set("status", e.target.value)} className={selCls}>
              <option value="Approved">Approved Only</option>
              <option value="All">All Statuses</option>
            </select></div>
          <div className="flex gap-2">
            <button data-testid="ii-apply" onClick={() => run()}
              className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-1.5">
              <RotateCw size={12} className={loading ? "animate-spin" : ""} /> Apply
            </button>
            <button data-testid="ii-reset" onClick={() => { setFilters(EMPTY); run(EMPTY); }}
              className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50">Reset</button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {cards.map((c) => (
          <div key={c.key} data-testid={`ii-card-${c.key}`} className={`border px-4 py-3 ${c.tone}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{c.label}</div>
            <div className="text-xl font-bold mt-0.5">{c.money ? formatINR(t[c.key] ?? 0) : (t[c.key] ?? 0)}</div>
          </div>
        ))}
      </div>

      {t.truncated && (
        <div className="mb-4 border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          Showing the first 5,000 lines — narrow the filters to see everything.
        </div>
      )}

      <Card>
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-2">
            <PackageOpen size={14} className="text-orange-600" /> Issued Lines ({rows.length})
          </h3>
          <div className="flex items-center gap-2">
            <span className={lblCls + " mb-0"}>Group By</span>
            <select data-testid="ii-group-by" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
              className="border border-slate-300 px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-orange-500">
              <option value="">None (flat)</option>
              <option value="project">Project</option>
              <option value="warehouse">Warehouse</option>
              <option value="party">Member / Client / Vendor</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="issued-items-table" className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                {["Voucher No", "Date", "Branch", "Project", "Warehouse", "Party", "Item / Asset", "Code", "Type", "Issue Type"].map((h) => (
                  <th key={h} className="px-3 py-2.5 font-bold">{h}</th>
                ))}
                <th className="px-3 py-2.5 font-bold text-right">Qty</th>
                <th className="px-3 py-2.5 font-bold">Unit</th>
                <th className="px-3 py-2.5 font-bold text-right">Rate</th>
                <th className="px-3 py-2.5 font-bold text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={14} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={14} className="px-4 py-12 text-center text-slate-400">No issued lines match these filters.</td></tr>
              ) : grouped ? (
                grouped.map(([key, list]) => (
                  <FragmentGroup key={key} label={`${GROUPS[groupBy].label}: ${key}`} list={list} Row={Row} SubTotal={SubTotal} />
                ))
              ) : (
                pg.paged.map((r, i) => <Row key={i} r={r} i={i} />)
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-slate-100">
          <PaginationControls page={pg.page} pageSize={pg.pageSize} total={pg.total}
            onPage={pg.setPage} onPageSize={pg.setPageSize} pageSizeOptions={[25, 50, 100, 200]} />
        </div>
      </Card>
    </div>
  );
}

function FragmentGroup({ label, list, Row, SubTotal }) {
  return (
    <>
      <tr className="bg-orange-50/60 border-t border-orange-200">
        <td colSpan={14} className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-orange-800">{label}</td>
      </tr>
      {list.map((r, i) => <Row key={i} r={r} i={`${label}-${i}`} />)}
      <SubTotal label={label} list={list} />
    </>
  );
}
